import type {
  EdfManifestEntry,
  P5EdfManifestEntry,
} from '@lmt/signal-viewer-processor';
import { apiClient } from './api-client';
import type { TherapyEventDto } from './events-api';

export type TherapySettingParameter = {
  order: number;
  name: string;
  parameterId: number | null;
  localizerKey: string;
  rawValue: number;
  displayValue: string;
  unit: string | null;
  isVisible: boolean;
};

export type TherapySettingsSnapshot = {
  timeSeconds: number;
  timestampLocal: string | null;
  mode: string;
  parameters: TherapySettingParameter[];
};

export type TherapySettingsSection = {
  startTimestampLocal: string | null;
  endTimestampLocal: string | null;
  snapshots: TherapySettingsSnapshot[];
};

export type TherapySettingsResult = {
  sections: TherapySettingsSection[];
};

/**
 * Response shape of the combined /therapy-archive endpoints: the backend reads both parts
 * from one upload and degrades each independently, so a failing parameter-set plugin
 * does not withhold events and vice versa. Error fields distinguish backend failures
 * from data that is simply unavailable.
 */
export type TherapyArchiveResponse = {
  therapySettings: TherapySettingsResult | null;
  therapySettingsError: string | null;
  events: TherapyEventDto[] | null;
  eventsError: string | null;
};

type TherapyArchiveRequestBase = {
  file: File;
  deviceType?: string | null;
  mode?: string | null;
};

export type TherapyArchiveRequest = TherapyArchiveRequestBase & {
  manifest: EdfManifestEntry[];
};

export type P5TherapyArchiveRequest = TherapyArchiveRequestBase & {
  manifest: P5EdfManifestEntry[];
};

export function getP28TherapyArchive(
  request: TherapyArchiveRequest,
): Promise<TherapyArchiveResponse> {
  return postTherapyArchive(
    '/therapy-archive/p2-8',
    request,
    toApiManifest(request.manifest),
  );
}

export function getP3TherapyArchive(
  request: TherapyArchiveRequest,
): Promise<TherapyArchiveResponse> {
  return postTherapyArchive(
    '/therapy-archive/p3',
    request,
    toApiManifest(request.manifest),
  );
}

export function getP32TherapyArchive(
  request: TherapyArchiveRequest,
): Promise<TherapyArchiveResponse> {
  return postTherapyArchive(
    '/therapy-archive/p3-2',
    request,
    toApiManifest(request.manifest),
  );
}

export function getP5TherapyArchive(
  request: P5TherapyArchiveRequest,
): Promise<TherapyArchiveResponse> {
  return postTherapyArchive(
    '/therapy-archive/p5',
    request,
    toP5ApiManifest(request.manifest),
  );
}

async function postTherapyArchive(
  url: string,
  request: TherapyArchiveRequestBase,
  manifest: object[],
): Promise<TherapyArchiveResponse> {
  const response = await apiClient.post<TherapyArchiveResponse>(
    url,
    toArchiveFormData(request, manifest),
  );

  return response.data;
}

function toArchiveFormData(
  request: TherapyArchiveRequestBase,
  manifest: object[],
): FormData {
  const formData = new FormData();
  formData.append('File', request.file);
  formData.append(
    'Manifest',
    new Blob([JSON.stringify(manifest)], {
      type: 'application/json',
    }),
    'edf-manifest.json',
  );

  appendIfPresent(formData, 'DeviceType', request.deviceType);
  appendIfPresent(formData, 'Mode', request.mode);

  return formData;
}

function toApiManifest(manifest: EdfManifestEntry[]) {
  return manifest.map((entry) => ({
    path: entry.path,
    startTimestampLocal: toLocalIso(entry.startMs),
    durationMs: entry.durationMs,
  }));
}

/**
 * P5-only API manifest extension. MCC's incident API (pTS plugins) requires the opaque device-clock
 * value as `GetAlarms(long start, TimeSpan span)`; other generations never send it.
 */
function toP5ApiManifest(manifest: P5EdfManifestEntry[]) {
  return manifest.map((entry) => ({
    path: entry.path,
    startTimestampLocal: toLocalIso(entry.startMs),
    durationMs: entry.durationMs,
    p5DeviceTimepoint: entry.p5DeviceTimepoint,
  }));
}

function toLocalIso(timestampMs: number): string {
  const date = new Date(timestampMs);
  const pad = (value: number) => String(value).padStart(2, '0');

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

function appendIfPresent(
  formData: FormData,
  key: string,
  value: string | null | undefined,
) {
  if (value) {
    formData.append(key, value);
  }
}
